import type { Schema } from '@hyphen-db-studio/schema-core';
import { resolveNodeByLabel } from './explanationRefs';

/**
 * A "story" is written inside a diagram's explanation as numbered steps: level-3 headings that begin "Step 1:",
 * "Step 2:" and so on, each followed by a short narration. The links inside a step (`[nginx](#node:nginx)` in an
 * architecture diagram, `[booking](#table:booking)` in a database diagram) say which parts of the diagram the step is
 * about, so playing the story can fly the camera there and light them up.
 */

export interface TourRef {
  kind: string;
  target: string;
}

export interface TourStep {
  /** 0-based position in the story. */
  index: number;
  title: string;
  /** The narration, as Markdown (without the heading). */
  markdown: string;
  /** The diagram links in the narration (nodes, tables, fields, groups), in reading order, without repeats. */
  refs: TourRef[];
}

const HEADING = /^(#{1,4})\s+(.*)$/;
export const STEP_HEADING = /^step\s+(\d+)\s*[:.\-–—]?\s*(.*)$/i;
const DIAGRAM_LINK = /\]\(#(node|table|column|group|enum):([^\s)]*)\)/g;

export function parseTourSteps(markdown: string): TourStep[] {
  const steps: TourStep[] = [];
  let current: { title: string; lines: string[] } | null = null;
  let fenced = false;

  const finish = () => {
    if (!current) return;
    const body = current.lines.join('\n').trim();
    const seen = new Set<string>();
    const refs: TourRef[] = [];
    for (const m of body.matchAll(DIAGRAM_LINK)) {
      let target = m[2];
      try {
        target = decodeURIComponent(target);
      } catch {
        // keep the raw text
      }
      const key = `${m[1]}:${target.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        refs.push({ kind: m[1], target });
      }
    }
    steps.push({ index: steps.length, title: current.title, markdown: body, refs });
    current = null;
  };

  for (const line of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.trimStart().startsWith('```')) fenced = !fenced;
    const heading = fenced ? null : HEADING.exec(line);
    if (heading) {
      finish();
      const visible = heading[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
      const step = heading[1].length === 3 ? STEP_HEADING.exec(visible) : null;
      if (step) current = { title: step[2].replace(/[`*_]/g, '').trim() || `Step ${step[1]}`, lines: [] };
      continue;
    }
    current?.lines.push(line);
  }
  finish();
  return steps;
}

export interface TourFocus {
  /** Nodes to light up (and keep un-dimmed). */
  nodeIds: Set<string>;
  /** Connectors between the lit-up nodes. */
  edgeIds: Set<string>;
  /** Database diagrams: individual fields the step points at, highlighted inside their tables. */
  columnIds?: Set<string>;
}

interface NodeLike {
  id: string;
  label: string;
  mode?: string;
  parentId?: string | null;
}
interface EdgeLike {
  id: string;
  fromNodeId: string;
  toNodeId: string;
}

/** The parts and conversations a step is about. A referenced group keeps all of its members un-dimmed. */
export function computeFocus(step: TourStep, nodes: NodeLike[], edges: EdgeLike[]): TourFocus {
  const referenced = step.refs
    .filter((r) => r.kind === 'node')
    .map((r) => resolveNodeByLabel(nodes, r.target))
    .filter((n): n is NodeLike => !!n);
  const nodeIds = new Set<string>();
  const explicit = new Set<string>();
  for (const n of referenced) {
    nodeIds.add(n.id);
    if (n.mode === 'group') nodes.filter((m) => m.parentId === n.id).forEach((m) => nodeIds.add(m.id));
    else explicit.add(n.id);
  }
  // a member's own group stays visible too, so the box around it does not dim away
  for (const id of [...explicit]) {
    const parent = nodes.find((n) => n.id === id)?.parentId;
    if (parent) nodeIds.add(parent);
  }
  const edgeIds = new Set(edges.filter((e) => explicit.has(e.fromNodeId) && explicit.has(e.toNodeId)).map((e) => e.id));
  return { nodeIds, edgeIds };
}

/** How long a step stays on screen while playing: a base pause plus reading time. */
export function stepDurationMs(step: TourStep): number {
  const words = step.markdown.split(/\s+/).filter(Boolean).length;
  return Math.min(18_000, Math.max(6_500, 4_000 + words * 260));
}

/**
 * The same for a database diagram. Tables (and the table of a field) are lit up; a group lights its whole box and all of
 * its tables; the relationships between lit tables are highlighted; referenced fields are marked inside their tables.
 * Canvas ids: a table is its own id, a group box is `group:<id>`, a relationship is its own id.
 */
export function computeErFocus(step: TourStep, schema: Schema): TourFocus {
  const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
  const nodeIds = new Set<string>();
  const explicit = new Set<string>();
  const columnIds = new Set<string>();

  for (const ref of step.refs) {
    if (ref.kind === 'table' || ref.kind === 'column') {
      const [tableName, columnName] = ref.kind === 'column' ? ref.target.split('.') : [ref.target, undefined];
      const table = schema.tables.find((t) => same(t.name, tableName));
      if (!table) continue;
      nodeIds.add(table.id);
      explicit.add(table.id);
      if (table.groupId) nodeIds.add(`group:${table.groupId}`);
      const column = columnName ? table.columns.find((c) => same(c.name, columnName)) : undefined;
      if (column) columnIds.add(column.id);
    } else if (ref.kind === 'group') {
      const group = schema.groups.find((g) => same(g.name, ref.target));
      if (!group) continue;
      nodeIds.add(`group:${group.id}`);
      schema.tables.filter((t) => t.groupId === group.id).forEach((t) => nodeIds.add(t.id));
    }
  }
  const edgeIds = new Set(schema.relationships.filter((r) => explicit.has(r.fromTableId) && explicit.has(r.toTableId)).map((r) => r.id));
  return { nodeIds, edgeIds, columnIds };
}
